import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Folder, FolderOpen, ImagePlus, Link2, Upload } from 'lucide-react';
import { useBulletin, useCreateBulletin, useSetBulletinCoverLink, useUpdateBulletin, useUploadBulletinCover } from '@/hooks/useBulletin';
import { bulletinApi, UploadBrowserResponse } from '@/api/bulletin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RichTextEditor } from '@/components/editor/RichTextEditor';
import { notifyError, notifyInfo, notifySuccess } from '@/lib/feedback';

type CoverMode = 'upload' | 'link' | 'existing';

export function BulletinEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const bulletinId = Number(id);
  const isEdit = Number.isFinite(bulletinId) && bulletinId > 0;

  const { data, isLoading } = useBulletin(bulletinId);
  const createBulletin = useCreateBulletin();
  const updateBulletin = useUpdateBulletin();
  const uploadCover = useUploadBulletinCover();
  const setCoverLinkMutation = useSetBulletinCoverLink();

  const [title, setJudul] = useState('');
  const [author, setPenulis] = useState('');
  const [publishTanggal, setPublishTanggal] = useState('');
  const [content, setContent] = useState('');

  const [coverDialogOpen, setCoverDialogOpen] = useState(false);
  const [coverMode, setCoverMode] = useState<CoverMode>('upload');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverLink, setCoverUrl] = useState('');
  const [coverSebelumnyaiew, setCoverSebelumnyaiew] = useState<string | null>(null);
  const [selectedUploadUrl, setSelectedUploadUrl] = useState('');
  const [uploadExplorer, setUploadExplorer] = useState<UploadBrowserResponse>({
    currentDir: '',
    parentDir: '',
    directories: [],
    files: [],
  });
  const [uploadsLoading, setUploadsLoading] = useState(false);

  const uploadLocalImage = async (file: File): Promise<string> => {
    try {
      const response = await bulletinApi.uploadInlineImage(file);
      notifySuccess('Gambar berhasil diunggah', undefined, false);
      return response.url || '';
    } catch (error) {
      notifyError('Gagal mengunggah gambar', error, false);
      throw error;
    }
  };

  useEffect(() => {
    if (!isEdit || !data) return;
    setJudul(data.nama || '');
    setPenulis(data.penulis || '');
    setPublishTanggal(data.tanggal || '');
    setContent(data.isi_berita || '');
    setCoverSebelumnyaiew(data.cover || null);
  }, [isEdit, data]);

  const busy = createBulletin.isPending || updateBulletin.isPending || uploadCover.isPending || setCoverLinkMutation.isPending;
  const pageTitle = useMemo(() => (isEdit ? 'Ubah Postingan' : 'Buat Postingan'), [isEdit]);

  const applyCoverChoice = () => {
    if (coverMode === 'upload' && coverFile) {
      const objectUrl = URL.createObjectURL(coverFile);
      setCoverSebelumnyaiew(objectUrl);
      setCoverUrl('');
      setSelectedUploadUrl('');
    }
    if (coverMode === 'link' && coverLink.trim()) {
      setCoverSebelumnyaiew(coverLink.trim());
      setCoverFile(null);
      setSelectedUploadUrl('');
    }
    if (coverMode === 'existing' && selectedUploadUrl.trim()) {
      setCoverSebelumnyaiew(selectedUploadUrl.trim());
      setCoverFile(null);
      setCoverUrl(selectedUploadUrl.trim());
    }
    setCoverDialogOpen(false);
  };

  const loadUploads = async (dir = '') => {
    setUploadsLoading(true);
    try {
      const data = await bulletinApi.browseUploads(dir);
      setUploadExplorer(data);
    } catch (error) {
      notifyError('Gagal memuat daftar upload', error, false);
      throw error;
    } finally {
      setUploadsLoading(false);
    }
  };

  useEffect(() => {
    if (!coverDialogOpen || coverMode !== 'existing') return;
    loadUploads(uploadExplorer.currentDir || '').catch((error) => {
      console.error('Failed to browse uploads:', error);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverDialogOpen, coverMode]);

  const savePostingan = async () => {
    if (!title.trim()) return;

    const payload = {
      nama: title.trim(),
      penulis: author.trim() || undefined,
      tanggal: publishTanggal || undefined,
      isi_berita: content || undefined,
    };

    try {
      let savedId = bulletinId;
      if (isEdit) {
        await updateBulletin.mutateAsync({ id: bulletinId, data: payload });
      } else {
        const result = await createBulletin.mutateAsync(payload);
        savedId = Number(result?.id || 0);
      }

      if (!savedId) {
        notifyInfo('Postingan tersimpan tanpa ID respons');
        return;
      }

      if (coverMode === 'upload' && coverFile) {
        await uploadCover.mutateAsync({ id: savedId, file: coverFile });
      } else if (coverMode === 'link' && coverLink.trim()) {
        await setCoverLinkMutation.mutateAsync({ id: savedId, url: coverLink.trim() });
      } else if (coverMode === 'existing' && selectedUploadUrl.trim()) {
        await setCoverLinkMutation.mutateAsync({ id: savedId, url: selectedUploadUrl.trim() });
      }

      navigate('/postingan');
    } catch (error) {
      console.error('Failed to save bulletin:', error);
    }
  };

  if (isEdit && isLoading) {
    return <div>Memuat...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="mb-2">
            <Link to="/postingan" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
              Kembali ke daftar
            </Link>
          </div>
          <h2 className="text-3xl font-bold tracking-tight">{pageTitle}</h2>
          <p className="text-muted-foreground">Tambah judul, gambar sampul, dan konten postingan.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/postingan')}>
            Batal
          </Button>
          <Button onClick={savePostingan} disabled={busy || !title.trim()}>
            Simpan Postingan
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Informasi Utama</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Judul Postingan</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setJudul(e.target.value)}
              placeholder="Masukkan judul postingan"
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="author">Penulis</Label>
              <Input id="author" value={author} onChange={(e) => setPenulis(e.target.value)} placeholder="Nama penulis" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date">Tanggal Publikasi</Label>
              <Input id="date" type="date" value={publishTanggal} onChange={(e) => setPublishTanggal(e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Cover Postingan</CardTitle>
          <Button variant="outline" onClick={() => setCoverDialogOpen(true)}>
            <ImagePlus className="mr-2 h-4 w-4" />
            Pilih Cover
          </Button>
        </CardHeader>
        <CardContent>
          {coverSebelumnyaiew ? (
            <img src={coverSebelumnyaiew} alt="Pratinjau sampul" className="h-56 w-full rounded-md object-cover" />
          ) : (
            <div className="flex h-56 w-full items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
              Belum ada cover dipilih
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Konten Postingan</CardTitle>
        </CardHeader>
        <CardContent>
          <RichTextEditor
            value={content}
            onChange={setContent}
            placeholder="Tulis isi postingan di sini..."
            onUploadLocalImage={uploadLocalImage}
          />
        </CardContent>
      </Card>

      <Dialog open={coverDialogOpen} onOpenChange={setCoverDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pilih Cover</DialogTitle>
            <DialogDescription>Gunakan file lokal atau URL gambar.</DialogDescription>
          </DialogHeader>
          <Tabs value={coverMode} onValueChange={(v) => setCoverMode(v as CoverMode)}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="upload">
                <Upload className="mr-2 h-4 w-4" />
                Upload Lokal
              </TabsTrigger>
              <TabsTrigger value="link">
                <Link2 className="mr-2 h-4 w-4" />
                Link URL
              </TabsTrigger>
              <TabsTrigger value="existing">
                <Folder className="mr-2 h-4 w-4" />
                Dari Server
              </TabsTrigger>
            </TabsList>
            <TabsContent value="upload" className="space-y-3 pt-3">
              <Input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-muted-foreground">Format yang disarankan: JPG/PNG/WebP.</p>
            </TabsContent>
            <TabsContent value="link" className="space-y-3 pt-3">
              <Input
                value={coverLink}
                onChange={(e) => setCoverUrl(e.target.value)}
                placeholder="https://example.com/cover.jpg"
              />
            </TabsContent>
            <TabsContent value="existing" className="space-y-3 pt-3">
              <div className="flex items-center justify-between rounded border p-2 text-xs">
                <div className="truncate">
                  {uploadExplorer.currentDir ? `uploads/${uploadExplorer.currentDir}` : 'uploads/'}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => loadUploads(uploadExplorer.parentDir || '')}
                    disabled={uploadsLoading || uploadExplorer.currentDir === ''}
                  >
                    Naik Folder
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => loadUploads(uploadExplorer.currentDir || '')}
                    disabled={uploadsLoading}
                  >
                    Refresh
                  </Button>
                </div>
              </div>

              <div className="max-h-72 overflow-auto rounded border">
                {uploadsLoading ? (
                  <div className="p-4 text-sm text-muted-foreground">Memuat daftar file server...</div>
                ) : (
                  <div className="p-2">
                    {uploadExplorer.directories.map((dir) => (
                      <button
                        key={`dir-${dir.dir}`}
                        type="button"
                        className="mb-1 flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-muted"
                        onClick={() => loadUploads(dir.dir)}
                      >
                        <FolderOpen className="h-4 w-4 text-blue-600" />
                        <span>{dir.name}</span>
                      </button>
                    ))}
                    {uploadExplorer.files.length > 0 ? (
                      <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-3">
                        {uploadExplorer.files.map((file) => {
                          const selected = selectedUploadUrl === file.url;
                          return (
                            <button
                              key={`file-${file.path}`}
                              type="button"
                              onClick={async () => {
                                try {
                                  const copied = await bulletinApi.copyUploadImage(file.path, 'cover');
                                  const copiedUrl = copied.url || '';
                                  setSelectedUploadUrl(copiedUrl);
                                  setCoverUrl(copiedUrl);
                                  setCoverSebelumnyaiew(copiedUrl);
                                  notifySuccess('Gambar server disalin ke folder publik', undefined, false);
                                } catch (error) {
                                  notifyError('Gagal menyalin gambar server', error, false);
                                }
                              }}
                              className={`overflow-hidden rounded border text-left ${selected ? 'ring-2 ring-primary' : ''}`}
                            >
                              <img src={file.url} alt={file.name} className="h-24 w-full object-cover" />
                              <div className="truncate px-2 py-1 text-xs">{file.name}</div>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-2 text-sm text-muted-foreground">Tidak ada gambar di folder ini.</div>
                    )}
                  </div>
                )}
              </div>

              {selectedUploadUrl ? (
                <div className="space-y-2">
                  <div className="text-xs text-muted-foreground">Preview terpilih</div>
                  <img src={selectedUploadUrl} alt="Selected upload" className="max-h-44 w-full rounded border object-contain" />
                </div>
              ) : null}
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCoverDialogOpen(false)}>
              Batal
            </Button>
            <Button onClick={applyCoverChoice}>Pakai Cover Ini</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
